import { forwardRef, Fragment, useCallback, useEffect, useRef, useState } from 'react';
import type {
  ClipboardEvent,
  FunctionComponent,
  MouseEvent,
  PropsWithoutRef,
  Ref,
  RefObject
} from 'react';
import styled, { css } from 'styled-components';

import type { ForwardProps } from '../../../types';
import { StyledFormControl } from '../../FormControl';
import type { FormControlProps } from '../../FormControl';
import FormField from '../../FormField';
import {
  useConfiguration,
  useConsolidatedRef,
  useFocusWithin,
  useI18n,
  useOuterEvent,
  useTestIds,
  useUID
} from '../../../hooks';
import Select, { Option } from '../../Select';
import Flex from '../../Flex';
import Button from '../../Button';
import Icon, { registerIcon } from '../../Icon';
import * as calendarIcon from '../../Icon/icons/calendar.icon';
import DatePicker from '../Picker/DatePicker';
import type { MinuteInterval } from '../Picker/DatePicker.types';
import type DateTimeProps from '../DateTime.types';
import type { DateTimeCallbackParameter } from '../DateTime.types';
import { createClassName, hasProp, withTestIds } from '../../../utils';
import { getDateTimeInputTestIds } from '../DateTime.test-ids';
import { getAdjustedUTCDateTime, getAdjustedUTCDateTimeForTimezone } from '../utils';
import HiddenText from '../../HiddenText';

import StyledDateTime, {
  StyledClearNowButtonsContainer,
  StyledDatePickerDialog,
  StyledDialogContent,
  StyledInputButton,
  StyledInputContainer
} from './DateTime.styles';
import {
  generatePickerButtonAriaLabel,
  getDateFormat,
  getDateTimeParts,
  getTimeOptions,
  is12HClockFormat,
  parsePastedTextToDate,
  parseToDate,
  validateDate,
  validateDateTime,
  validateTime
} from './utils';
import type { ClockFormat } from './utils';
import useAutoFocusNextInput from './useAutoFocusNextInput';
import PartInput from './PartInput';
import usePickerButton from './usePickerButton';
import type { DateParts, DateTimeParts, TimeParts } from './DateTimeInput.types';

registerIcon(calendarIcon);

export interface DateTimeInputProps extends DateTimeProps {
  /**
   * Display the time with seconds.
   * @default false
   */
  withSeconds?: boolean;
  /**
   * Display additional column with week number in date picker popover.
   * @default false
   */
  showWeekNumber?: boolean;
  /**
   * Defines interval for time picker options (in minutes).
   * @default 30
   */
  pickerInterval?: MinuteInterval;
  /** Defines clock format that overrides locale based format. */
  clockFormat?: ClockFormat;
  /** Pass a heading and content to show additional information on the field. */
  additionalInfo?: FormControlProps['additionalInfo'];
}

export const StyledDateTimeInput = styled(StyledDateTime)<DateTimeInputProps>(() => {
  return css`
    padding-inline-end: 0;
    min-width: 0;
  `;
});

const StyledDateTimeFieldset = styled.fieldset`
  min-width: 0;
`;

function getMeridiem(hour: number) {
  return hour >= 12 ? 'PM' : 'AM';
}

const convertTo12h = (hour: number) => {
  let converted: number = hour;
  if (hour > 12) converted = hour - 12;
  else if (hour === 0) converted = 12;
  return converted;
};

const convertTo24h = (hour: number, meridiem: string) => {
  let converted: number = hour;
  if (meridiem === 'PM' && hour < 12) converted = hour + 12;
  else if (meridiem === 'AM' && hour === 12) converted = 0;
  return converted;
};

export const convertToCallbackParameter = (
  date: Date,
  parts: DateTimeParts<string | undefined>
): DateTimeCallbackParameter => {
  const resultDate = Number.isNaN(date.getTime())
    ? new Date(new Date().setUTCHours(0, 0, 0, 0))
    : new Date(date);
  const withSeconds = hasProp(parts, 'second');
  const {
    year,
    month: monthIndex,
    day,
    hour,
    minute,
    second = resultDate.getUTCSeconds().toString(),
    dayPeriod: meridiem
  } = parts;
  const toValidate = withSeconds
    ? [year, monthIndex, day, hour, minute, second]
    : [year, monthIndex, day, hour, minute];

  if (toValidate.every(Boolean)) {
    const is12h = meridiem !== undefined;

    if (
      !validateDateTime(
        Number(year),
        Number(monthIndex) + 1,
        Number(day),
        Number(hour),
        Number(minute),
        Number(second),
        0,
        is12h
      )
    ) {
      return {
        valueAsISOString: '',
        valueAsTimestamp: NaN,
        state: 'invalid'
      };
    }

    resultDate.setUTCFullYear(Number(year), Number(monthIndex), Number(day));
    resultDate.setUTCHours(
      is12h ? convertTo24h(Number(hour), meridiem!) : Number(hour),
      Number(minute),
      Number(second),
      resultDate.getUTCMilliseconds()
    );

    return {
      valueAsISOString: resultDate.toISOString(),
      valueAsTimestamp: resultDate.getTime()
    };
  }
  if ([year, monthIndex, day, hour, minute].every(p => !p)) {
    return {
      valueAsISOString: undefined,
      valueAsTimestamp: undefined
    };
  }
  return {
    valueAsISOString: '',
    valueAsTimestamp: NaN,
    state: 'incomplete'
  };
};

function dateOptions() {
  return {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric'
  } as const;
}

const DateTimeInput: FunctionComponent<DateTimeInputProps & ForwardProps> = forwardRef(
  function DateTimeInput(
    props: PropsWithoutRef<DateTimeInputProps>,
    ref: Ref<HTMLFieldSetElement>
  ) {
    const { locale } = useConfiguration();
    const t = useI18n();
    const {
      testId,
      value,
      min,
      max,
      id,
      label,
      labelHidden,
      withSeconds = false,
      clockFormat,
      info = getDateFormat(t, locale, withSeconds ? 'datetime+seconds' : 'datetime'),
      status,
      required,
      readOnly,
      disabled,
      showWeekNumber,
      pickerInterval = 30,
      timeZone,
      onChange,
      onFocus,
      onBlur,
      onResolveSuggestion,
      additionalInfo,
      autoFocus,
      className,
      ...restProps
    } = props;

    const testIds = useTestIds(testId, getDateTimeInputTestIds);
    const yearPartId = useUID();

    const containerRef = useConsolidatedRef(ref);

    const dateTime = value !== undefined && value !== '' ? parseToDate(value) : undefined;
    const is12h = clockFormat ? clockFormat === 12 : is12HClockFormat(locale);

    const minYear = min !== undefined ? parseToDate(min).getUTCFullYear() : 1;
    const maxYear = max !== undefined ? parseToDate(max).getUTCFullYear() : 9999;

    const [
      yearInputRef,
      monthInputRef,
      dayInputRef,
      hourInputRef,
      minuteInputRef,
      secondInputRef,
      meridiemSelectRef
    ] = [
      useRef<HTMLInputElement>(null),
      useRef<HTMLInputElement>(null),
      useRef<HTMLInputElement>(null),
      useRef<HTMLInputElement>(null),
      useRef<HTMLInputElement>(null),
      useRef<HTMLInputElement>(null),
      useRef<HTMLSelectElement>(null)
    ];

    const [hasValue, setHasValue] = useState(!!dateTime);
    useEffect(() => {
      setHasValue(
        !!(
          yearInputRef.current?.value ||
          monthInputRef.current?.value ||
          dayInputRef.current?.value ||
          hourInputRef.current?.value ||
          minuteInputRef.current?.value ||
          (withSeconds && secondInputRef.current?.value)
        )
      );
    }, [dateTime]);

    useEffect(() => {
      if (is12h && meridiemSelectRef.current && dateTime?.getUTCHours() !== undefined) {
        meridiemSelectRef.current.value = getMeridiem(dateTime?.getUTCHours());
      }
    }, [is12h, dateTime?.getUTCHours()]);

    const [pickerSelection, setPickerSelection] = useState<Date | undefined>(undefined);

    const pickDateParts = (): DateParts<string | undefined> => {
      return {
        day: dayInputRef.current?.value || undefined,
        month: monthInputRef.current?.value
          ? (Number(monthInputRef.current.value) - 1).toString()
          : undefined,
        year: yearInputRef.current?.value || undefined
      };
    };

    const pickTimeParts = (): TimeParts<string | undefined> => {
      const parts: TimeParts<string | undefined> = {
        hour: hourInputRef.current?.value || undefined,
        minute: minuteInputRef.current?.value || undefined
      };
      if (withSeconds) {
        parts.second = secondInputRef.current?.value;
      }
      if (meridiemSelectRef.current) {
        parts.dayPeriod = meridiemSelectRef.current?.value;
      }

      return parts;
    };

    const pickParts = (): DateTimeParts<string | undefined> => {
      return { ...pickDateParts(), ...pickTimeParts() };
    };

    const getNow = useCallback(
      () => (timeZone ? getAdjustedUTCDateTimeForTimezone(timeZone) : getAdjustedUTCDateTime()),
      [timeZone]
    );

    const currentlySelectedDate = () => {
      const parts = pickDateParts();
      if (Object.values(parts).every(Boolean)) {
        const year = Number(parts.year);
        const month = Number(parts.month);
        const day = Number(parts.day);

        return validateDate(year, month + 1, day)
          ? new Date(Date.UTC(year, month, day))
          : undefined;
      }
    };

    const currentlySelectedTime = () => {
      const parts = pickParts();

      if (Object.values(parts).every(Boolean)) {
        const hour = Number(parts.hour);
        const minute = Number(parts.minute);
        const second = parts.second ? Number(parts.second) : 0;
        const meridiem = parts.dayPeriod;

        const current = dateTime !== undefined ? new Date(dateTime) : getNow();

        return validateTime(hour, minute, second, 0, meridiem !== undefined)
          ? new Date(
              current.setUTCHours(
                meridiem !== undefined ? convertTo24h(hour, meridiem) : hour,
                minute,
                second,
                dateTime?.getUTCMilliseconds() || 0
              )
            )
          : undefined;
      }
    };

    const currentlySelectedDateTime = () => {
      const selectedDate = currentlySelectedDate();
      const selectedTime = currentlySelectedTime();

      return selectedDate && selectedTime
        ? new Date(
            selectedDate.setUTCHours(
              selectedTime.getUTCHours(),
              selectedTime.getUTCMinutes(),
              selectedTime.getUTCSeconds(),
              selectedTime.getUTCMilliseconds()
            )
          )
        : undefined;
    };

    const setDefaultTimeIfEmpty = () => {
      setPickerSelection(undefined);
      // set value to midnight if time empty, skip if date not fully set
      if (
        Object.values(pickDateParts()).every(Boolean) &&
        Object.values(pickTimeParts()).every(p => !p)
      ) {
        if (hourInputRef.current) hourInputRef.current.value = is12h ? '12' : '00';

        if (minuteInputRef.current) minuteInputRef.current.value = '00';

        if (withSeconds && secondInputRef.current) secondInputRef.current.value = '00';

        if (is12h && meridiemSelectRef.current) {
          meridiemSelectRef.current.value = 'AM';
        }
      }
    };

    const [open, setOpen, , setPopoverRef, buttonEl, setButtonEl] =
      usePickerButton(setDefaultTimeIfEmpty);

    const onInputChange = () => {
      onChange?.(
        convertToCallbackParameter(dateTime ? new Date(dateTime) : new Date(), pickParts())
      );
      buttonEl?.setAttribute(
        'aria-label',
        generatePickerButtonAriaLabel(
          currentlySelectedDateTime(),
          'datetime',
          locale,
          t,
          open,
          is12h
        )
      );

      setHasValue(
        !!(
          dayInputRef.current?.value ||
          monthInputRef.current?.value ||
          yearInputRef.current?.value ||
          hourInputRef.current?.value ||
          minuteInputRef.current?.value ||
          (withSeconds && secondInputRef.current?.value)
        )
      );
    };

    const setInputValue = (newValue: Date | undefined) => {
      dayInputRef.current!.value = newValue?.getUTCDate().toString().padStart(2, '0') ?? '';
      monthInputRef.current!.value =
        (newValue && (newValue.getUTCMonth() + 1).toString().padStart(2, '0')) || '';
      yearInputRef.current!.value = newValue?.getUTCFullYear().toString() ?? '';

      if (newValue) {
        const hours = newValue.getUTCHours();
        hourInputRef.current!.value = (is12h ? convertTo12h(hours) : hours)
          .toString()
          .padStart(2, '0');
      } else {
        hourInputRef.current!.value = '';
      }

      minuteInputRef.current!.value = newValue?.getUTCMinutes().toString().padStart(2, '0') ?? '';
      if (withSeconds) {
        secondInputRef.current!.value = newValue?.getUTCSeconds().toString().padStart(2, '0') ?? '';
      }
      if (meridiemSelectRef.current) {
        meridiemSelectRef.current!.value = newValue ? getMeridiem(newValue.getUTCHours()) : 'AM';
      }
    };

    const onPaste = (e: ClipboardEvent) => {
      const text = e.clipboardData.getData('text');
      const date = parsePastedTextToDate(text, locale, dateTime || new Date(), 'datetime');
      if (date) {
        e.preventDefault();
        setInputValue(date);
        setPickerSelection(date);
        onChange?.(convertToCallbackParameter(date, pickParts()));
        setHasValue(true);
      }
    };

    const inputContainerRef = useRef<HTMLDivElement>(null);

    const onFocusChange = (focused: boolean) => {
      const callbackParam = convertToCallbackParameter(
        dateTime ? new Date(dateTime) : new Date(),
        pickParts()
      );
      if (onFocus && focused) onFocus(callbackParam);
      if (!focused) {
        if (inputContainerRef.current) inputContainerRef.current.scrollTop = 0;
        onBlur?.(callbackParam);
      }
    };

    // autoclose picker on focusing out
    useOuterEvent('focusin', [containerRef], e => {
      if (containerRef.current?.contains(e.relatedTarget as HTMLElement)) {
        setPickerSelection(undefined);
        setOpen(false);
      }
    });

    const displayNames = new Intl.DisplayNames(locale, { style: 'long', type: 'dateTimeField' });

    const DayInput = (
      <PartInput
        data-testid={testIds.controlDay}
        key='day'
        placeholder={t('day_placeholder')}
        aria-label={displayNames.of('day')}
        value={dateTime?.getUTCDate()}
        onChange={onInputChange}
        size={2}
        maxLength={2}
        readOnly={readOnly}
        min={1}
        max={31}
        ref={dayInputRef}
      />
    );

    const MonthInput = (
      <PartInput
        data-testid={testIds.controlMonth}
        key='month'
        placeholder={t('month_placeholder')}
        aria-label={displayNames.of('month')}
        value={dateTime ? dateTime.getUTCMonth() + 1 : undefined}
        onChange={onInputChange}
        size={2}
        maxLength={2}
        readOnly={readOnly}
        min={1}
        max={12}
        ref={monthInputRef}
      />
    );

    const YearInput = (
      <Fragment key='year'>
        <PartInput
          data-testid={testIds.controlYear}
          placeholder={t('year_placeholder')}
          aria-label={displayNames.of('year')}
          aria-describedby={yearPartId}
          value={dateTime?.getUTCFullYear()}
          onChange={onInputChange}
          size={4}
          maxLength={4}
          padWithZeros={false}
          readOnly={readOnly}
          min={minYear}
          max={maxYear}
          ref={yearInputRef}
        />
        <HiddenText id={yearPartId}>{t('year_input_description')}</HiddenText>
      </Fragment>
    );

    const hour = dateTime && is12h ? convertTo12h(dateTime.getUTCHours()) : dateTime?.getUTCHours();

    const HourInput = (
      <PartInput
        data-testid={testIds.controlHour}
        key='hour'
        placeholder={t('hour_placeholder')}
        aria-label={displayNames.of('hour')}
        value={hour}
        onChange={onInputChange}
        size={2}
        maxLength={2}
        readOnly={readOnly}
        min={is12h ? 1 : 0}
        max={is12h ? 12 : 23}
        ref={hourInputRef}
      />
    );

    const MinuteInput = (
      <PartInput
        data-testid={testIds.controlMinute}
        key='minute'
        placeholder={t('minute_placeholder')}
        aria-label={displayNames.of('minute')}
        value={dateTime?.getUTCMinutes()}
        onChange={onInputChange}
        size={2}
        maxLength={2}
        readOnly={readOnly}
        max={59}
        ref={minuteInputRef}
      />
    );

    const SecondInput = (
      <PartInput
        data-testid={testIds.controlSecond}
        key='second'
        placeholder={t('second_placeholder')}
        aria-label={displayNames.of('second')}
        value={dateTime?.getUTCSeconds()}
        onChange={onInputChange}
        size={2}
        maxLength={2}
        readOnly={readOnly}
        max={59}
        ref={secondInputRef}
      />
    );

    const Period = (
      <Select
        data-testid={testIds.controlPeriod}
        ref={meridiemSelectRef}
        aria-label={displayNames.of('dayPeriod')}
        key='period'
        required={required}
        disabled={disabled}
        onChange={() => {
          if (Object.values(pickParts()).every(Boolean)) onInputChange();
        }}
        readOnly={readOnly}
      >
        <Option value='AM'>{t('meridiem_value_am')}</Option>
        <Option value='PM'>{t('meridiem_value_pm')}</Option>
      </Select>
    );

    const orderedRefs: RefObject<HTMLInputElement>[] = [];

    const dateInputs = getDateTimeParts(locale, dateOptions()).map(part => {
      switch (part) {
        case 'day':
          orderedRefs.push(dayInputRef);
          return DayInput;
        case 'month':
          orderedRefs.push(monthInputRef);
          return MonthInput;
        case 'year':
          orderedRefs.push(yearInputRef);
          return YearInput;
        default:
          return part;
      }
    });

    const timeInputs = getDateTimeParts(locale, getTimeOptions(withSeconds, is12h)).map(part => {
      switch (part) {
        case 'hour':
          orderedRefs.push(hourInputRef);
          return HourInput;
        case 'minute':
          orderedRefs.push(minuteInputRef);
          return MinuteInput;
        case 'second':
          orderedRefs.push(secondInputRef);
          return SecondInput;
        case 'dayPeriod':
          return Period;
        default:
          return part;
      }
    });

    useEffect(() => {
      if (autoFocus) orderedRefs[0].current?.focus();
    }, [autoFocus]);

    useFocusWithin([containerRef], onFocusChange);

    const Picker = (
      <>
        <StyledInputButton
          data-testid={testIds.openClosePicker}
          variant='simple'
          icon
          ref={setButtonEl}
          onClick={() => {
            setOpen(cur => !cur);
          }}
          aria-label={generatePickerButtonAriaLabel(
            currentlySelectedDateTime(),
            'datetime',
            locale,
            t,
            open,
            is12h
          )}
          readOnly={readOnly}
          disabled={disabled}
        >
          <Icon name='calendar' />
        </StyledInputButton>
        {open && buttonEl && (
          <StyledDatePickerDialog
            ref={setPopoverRef}
            target={buttonEl}
            placement='bottom-end'
            aria-label={t('choose_date_and_time')}
          >
            <Flex container={{ direction: 'column' }} as={StyledDialogContent}>
              <DatePicker
                testId={testIds.picker}
                mode='datetime'
                selected={currentlySelectedDateTime()}
                onChange={selected => {
                  setPickerSelection(selected);
                }}
                min={min}
                max={max}
                showWeekNumber={showWeekNumber}
                pickerInterval={pickerInterval}
                withSeconds={withSeconds}
                clockFormat={clockFormat}
                timeZone={timeZone}
              />
              <Flex as={StyledClearNowButtonsContainer} container={{ justify: 'between' }}>
                <Button
                  data-testid={testIds.discardChanges}
                  aria-label={t('cancel')}
                  onClick={() => {
                    setPickerSelection(undefined);
                    setOpen(false);
                    setTimeout(() => buttonEl?.focus());
                    onInputChange();
                  }}
                >
                  {t('cancel')}
                </Button>
                <Button
                  data-testid={testIds.applyChanges}
                  aria-label={t('apply')}
                  variant='primary'
                  onClick={() => {
                    if (pickerSelection) {
                      setInputValue(pickerSelection);
                    }
                    setOpen(false);
                    setTimeout(() => buttonEl?.focus());
                    onInputChange();
                  }}
                >
                  {t('apply')}
                </Button>
              </Flex>
            </Flex>
          </StyledDatePickerDialog>
        )}
      </>
    );

    const Comp = (
      <Flex
        data-testid={label ? undefined : testIds.root}
        as={StyledDateTimeInput}
        forwardedAs={StyledFormControl}
        hasSuggestion={status === 'pending' && !!onResolveSuggestion}
        container={{ alignItems: 'center', wrap: 'nowrap', justify: 'between' }}
        status={status}
        readOnly={readOnly}
        disabled={disabled}
        ref={containerRef}
        onClick={(e: MouseEvent) => {
          if (e.target === containerRef.current) orderedRefs[0].current?.focus();
        }}
        {...restProps}
        className={createClassName('date-time-input', className)}
      >
        <Flex
          ref={inputContainerRef}
          as={StyledInputContainer}
          container={{ alignItems: 'center', wrap: 'wrap' }}
          onPaste={onPaste}
        >
          {dateInputs}
          {timeInputs}
        </Flex>
        {!readOnly && (
          <Flex container>
            {hasValue && (
              <StyledInputButton
                icon
                onClick={() => {
                  setPickerSelection(undefined);
                  setInputValue(undefined);
                  setTimeout(() => buttonEl?.focus());
                  onInputChange();
                }}
                variant='simple'
                compact
                aria-label={t('clear_current_date_and_time')}
                label={t('clear')}
                data-testid={testIds.clearCurrentDateAndTime}
              >
                <Icon name='times' />
              </StyledInputButton>
            )}
            {Picker}
          </Flex>
        )}
      </Flex>
    );

    const { description, descriptionID } = useAutoFocusNextInput(orderedRefs);

    return label ? (
      <>
        <FormField
          testId={testIds}
          as={StyledDateTimeFieldset}
          aria-labelledby={id}
          ref={ref}
          label={label}
          labelHidden={labelHidden}
          id={id}
          info={info}
          readOnly={readOnly}
          status={status}
          required={required}
          disabled={disabled}
          onResolveSuggestion={onResolveSuggestion}
          additionalInfo={additionalInfo}
          aria-describedby={descriptionID}
        >
          {Comp}
        </FormField>
        {description}
      </>
    ) : (
      Comp
    );
  }
);

export default withTestIds(DateTimeInput, getDateTimeInputTestIds);
